"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { GameImage } from "@/components/shared/game-image";
import { X, ChevronDown, ChevronUp, ArrowLeftRight } from "lucide-react";
import { useCompareStore } from "../hooks/useCompareStore";
import { COMPARE_COLORS } from "../types";

export function CompareFloatingPanel() {
  const { items, remove, clear } = useCompareStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const pathname = usePathname();

  // Don't show on the compare page itself
  if (pathname === "/compare") return null;
  if (items.length === 0) return null;

  const compareUrl = `/compare?games=${items.map((i) => i.appid).join(",")}`;
  const canCompare = items.length >= 2;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-72 bg-[#16202d] border border-[#2a475e] rounded-lg shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#1b2838] border-b border-[#2a475e]">
        <div className="flex items-center gap-2">
          <ArrowLeftRight size={14} className="text-[#66c0f4]" />
          <span className="text-sm font-medium text-white">
            Compare ({items.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clear}
            className="text-[#8f98a0] hover:text-red-400 transition-colors text-xs px-1"
            title="Clear all"
          >
            Clear
          </button>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-[#8f98a0] hover:text-white transition-colors p-0.5"
          >
            {isCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Game list */}
      {!isCollapsed && (
        <div className="p-2 space-y-1.5 max-h-[240px] overflow-y-auto">
          {items.map((item, i) => (
            <div
              key={item.appid}
              className="flex items-center gap-2 bg-[#1b2838]/50 rounded px-2 py-1.5"
            >
              <div
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: COMPARE_COLORS[i % COMPARE_COLORS.length] }}
              />
              <GameImage
                src={item.capsule_image}
                alt={item.name}
                width={40}
                height={15}
                className="rounded-sm flex-shrink-0"
              />
              <span className="text-xs text-white truncate flex-1">
                {item.name}
              </span>
              <button
                onClick={() => remove(item.appid)}
                className="text-[#8f98a0] hover:text-red-400 transition-colors flex-shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Compare button */}
      {!isCollapsed && (
        <div className="p-2 pt-0">
          {canCompare ? (
            <Link
              href={compareUrl}
              className="flex items-center justify-center gap-2 w-full py-2 rounded-md bg-[#66c0f4] hover:bg-[#7fd0ff] text-[#0e1419] text-sm font-medium transition-colors"
            >
              <ArrowLeftRight size={14} />
              Compare
            </Link>
          ) : (
            <div className="text-center text-[#8f98a0] text-xs py-1.5">
              Add one more game to compare
            </div>
          )}
        </div>
      )}
    </div>
  );
}
